import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@wlb/shared/context/AuthContext';
import registrationService from '@wlb/shared/api/services/registrationService';
import {
  getProfileInfoFieldErrors,
  hasBasicInfoFieldErrors,
  type ProfileInfoFieldErrors,
} from '@wlb/shared/api/onboarding/onboardingErrors';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getApiErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { parseDateOnlyString } from '@wlb/shared/utils/dateHelpers';
import type { ApiError } from '@wlb/shared/types/api';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { FormError } from '../components/ui/FormError';
import { AuthFlowLayout } from '../components/layout/AuthFlowLayout';
import type { OnboardingLocationState } from '../features/auth/onboardingFlowState';

export function UploadImage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { userGUID } = useAuth();
  const profile = (state as OnboardingLocationState | null)?.profile;
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');
  const previewRef = useRef('');

  useEffect(() => {
    previewRef.current = preview;
  }, [preview]);

  useEffect(() => () => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
  }, []);

  if (!profile) {
    return <Navigate to="/basic-info" replace />;
  }

  const goBackToBasicInfo = (fieldErrors?: ProfileInfoFieldErrors) => {
    const backState: OnboardingLocationState = { profile, fieldErrors };
    navigate('/basic-info', { state: backState });
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    if (!picked) return;
    if (preview) URL.revokeObjectURL(preview);
    setFile(picked);
    setPreview(URL.createObjectURL(picked));
    setServerError('');
  };

  /**
   * Single OnboardingProfileInfo call carrying the /basic-info answers and the
   * avatar; the endpoint returns no body, so the preview stays the local blob.
   */
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;

    if (!userGUID) {
      navigate('/survey');
      return;
    }

    setSaving(true);
    setServerError('');
    try {
      await registrationService.saveProfileInfo({
        userGUID,
        gender: profile.gender,
        countryId: profile.countryId,
        heightMetres: profile.heightMetres,
        heightUnit: profile.heightUnit,
        weight: profile.weight,
        weightUnit: profile.weightUnit,
        dob: parseDateOnlyString(profile.dob) ?? new Date(profile.dob),
        // The mapper derives the multipart filename and MIME type from the URI,
        // and a browser object URL carries neither, so pass the picked name.
        profilePictureUri: file ? `blob:/${file.name}` : '',
        profilePictureBlob: file,
      });
      navigate('/survey');
    } catch (error) {
      const message = getApiErrorMessage(error as ApiError);
      const fieldErrors = getProfileInfoFieldErrors(error as ApiError);
      ToastService.show({ type: 'error', message });
      // The rejected values were entered on /basic-info, so the user is sent
      // back there with the server's per-field messages instead of being stuck
      // on a step that cannot fix them.
      if (hasBasicInfoFieldErrors(fieldErrors)) {
        goBackToBasicInfo(fieldErrors);
        return;
      }
      setServerError(fieldErrors.profilePicture || message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AuthFlowLayout>
      <div className="auth-flow__card">
        <div className="auth-flow__step-nav">
          <button
            className="auth-flow__back"
            type="button"
            onClick={() => goBackToBasicInfo()}
            disabled={saving}
            aria-label={t('uploadImage.backToBasicInfo')}
          >
            ‹ {t('common.buttons.back')}
          </button>
          <button className="auth-flow__skip" type="button" onClick={() => navigate('/survey')}>{t('uploadImage.skip')}</button>
        </div>
        <h1 className="auth-flow__heading">{t('uploadImage.heading')}</h1>
        <p className="auth-flow__subheading">{t('uploadImage.subtitle')}</p>
        <p className="auth-flow__upload-hint">{t('uploadImage.hint')}</p>
        <form className="auth-flow__form" onSubmit={submit}>
          <label className="auth-flow__upload-zone">
            {preview ? <img src={preview} alt="" /> : <span aria-hidden>↑</span>}
            <strong>{preview ? t('uploadImage.uploadAgain') : t('uploadImage.tapToUpload')}</strong>
            <small>{t('uploadImage.uploadHint')}</small>
            <input type="file" accept="image/png,image/jpeg" onChange={onFileChange} disabled={saving} />
          </label>
          <FormError variant="banner" message={serverError} />
          <button className="auth-flow__primary" type="submit" disabled={saving}>
            {saving ? t('uploadImage.uploading') : t('common.buttons.next')}
          </button>
        </form>
      </div>
    </AuthFlowLayout>
  );
}
